import React from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import DesktopWindowsRoundedIcon from "@mui/icons-material/DesktopWindowsRounded";
import TabletMacRoundedIcon from "@mui/icons-material/TabletMacRounded";
import PhoneIphoneRoundedIcon from "@mui/icons-material/PhoneIphoneRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CloudDoneRoundedIcon from "@mui/icons-material/CloudDoneRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import RocketLaunchRoundedIcon from "@mui/icons-material/RocketLaunchRounded";
import PaletteRoundedIcon from "@mui/icons-material/PaletteRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import UndoRoundedIcon from "@mui/icons-material/UndoRounded";
import RedoRoundedIcon from "@mui/icons-material/RedoRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import AccessibilityNewRoundedIcon from "@mui/icons-material/AccessibilityNewRounded";
import { buildDefaultDesignTokens } from "../landingEditor/config";
import { PROJECT_DEPLOYMENT_STATUS } from "../../../utils/projectDeployment";
import { getLandingPreviewDevice } from "./previewDevices";
import GoogleFontPicker from "../landingEditor/GoogleFontPicker";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

function deploymentPresentation(deployment = {}, job = null, externalError = "") {
  const jobStatus = String(job?.status || "").trim().toLowerCase();
  const jobError = String(job?.error?.message || externalError || "").trim();

  if (jobStatus === "queued") {
    return {
      status: "queued",
      label: "Despliegue en cola",
      severity: "info",
      description: "La versión pública ya fue guardada y espera iniciar su actualización.",
      error: jobError,
      pending: true,
    };
  }
  if (jobStatus === "running") {
    return {
      status: "running",
      label: "Actualizando web",
      severity: "info",
      description: "El servidor está preparando el despliegue de la versión publicada.",
      error: jobError,
      pending: true,
    };
  }
  if (jobStatus === "failed") {
    return {
      status: "failed",
      label: "Falló el despliegue",
      severity: "error",
      description: "La revisión quedó publicada, pero la actualización técnica no terminó correctamente.",
      error: jobError,
      pending: false,
    };
  }

  const rawStatus = String(deployment?.status || "not_requested").trim();
  const status = PROJECT_DEPLOYMENT_STATUS[rawStatus] ? rawStatus : "not_requested";
  const meta = PROJECT_DEPLOYMENT_STATUS[status];
  return {
    status,
    label: meta.label,
    severity: meta.severity,
    description: meta.description,
    error: String(externalError || deployment?.errorMessage || "").trim(),
    pending: status === "provisioning",
  };
}

const TOKEN_GROUPS = [
  { id: "colors", label: "Colores", description: "Paleta global usada cuando un segmento no tiene un color propio." },
  { id: "typography", label: "Tipografía", description: "Familias, tamaños, pesos e interlineados generales." },
  { id: "spacing", label: "Espaciado", description: "Escala común de separaciones y aire entre contenidos." },
  { id: "borders", label: "Bordes", description: "Grosor, estilo, radios y color base de los contenedores." },
  { id: "shadows", label: "Sombras", description: "Profundidad de tarjetas, superficies y foco visual." },
];

const TOKEN_FIELDS = {
  colors: [
    ["primary", "Principal", "color"],
    ["secondary", "Secundario", "color"],
    ["background", "Fondo general", "color"],
    ["surface", "Superficie", "color"],
    ["text", "Texto principal", "color"],
    ["mutedText", "Texto secundario", "color"],
    ["divider", "Divisores", "color"],
    ["onPrimary", "Texto sobre principal", "color"],
    ["onSecondary", "Texto sobre secundario", "color"],
    ["success", "Éxito", "color"],
    ["warning", "Advertencia", "color"],
    ["error", "Error", "color"],
  ],
  typography: [
    ["baseFontSize", "Tamaño base", "number", { min: 10, max: 24, step: 1, suffix: "px" }],
    ["scaleRatio", "Escala de títulos", "number", { min: 1, max: 2, step: 0.05 }],
    ["lineHeight", "Interlineado", "number", { min: 1, max: 2.5, step: 0.05 }],
    ["headingLineHeight", "Interlineado de títulos", "number", { min: 0.8, max: 2, step: 0.05 }],
    ["fontWeightRegular", "Peso regular", "number", { min: 100, max: 1000, step: 100 }],
    ["fontWeightMedium", "Peso medio", "number", { min: 100, max: 1000, step: 100 }],
    ["fontWeightBold", "Peso fuerte", "number", { min: 100, max: 1000, step: 100 }],
    ["letterSpacing", "Espaciado de letras", "number", { min: -4, max: 12, step: 0.1, suffix: "px" }],
  ],
  spacing: [
    ["unit", "Unidad base", "number", { min: 1, max: 24, step: 1, suffix: "px" }],
    ["xs", "Extra chico", "number", { min: 0, max: 64, step: 1, suffix: "px" }],
    ["sm", "Chico", "number", { min: 0, max: 96, step: 1, suffix: "px" }],
    ["md", "Medio", "number", { min: 0, max: 160, step: 1, suffix: "px" }],
    ["lg", "Grande", "number", { min: 0, max: 240, step: 1, suffix: "px" }],
    ["xl", "Extra grande", "number", { min: 0, max: 320, step: 1, suffix: "px" }],
    ["xxl", "Máximo", "number", { min: 0, max: 480, step: 1, suffix: "px" }],
    ["section", "Separación de secciones", "number", { min: 0, max: 640, step: 1, suffix: "px" }],
  ],
  borders: [
    ["width", "Grosor", "number", { min: 0, max: 12, step: 0.5, suffix: "px" }],
    ["style", "Estilo", "select", { options: ["solid", "dashed", "dotted", "none"] }],
    ["radiusSm", "Radio chico", "number", { min: 0, max: 120, step: 1, suffix: "px" }],
    ["radiusMd", "Radio medio", "number", { min: 0, max: 160, step: 1, suffix: "px" }],
    ["radiusLg", "Radio grande", "number", { min: 0, max: 240, step: 1, suffix: "px" }],
    ["radiusPill", "Radio píldora", "number", { min: 0, max: 9999, step: 1, suffix: "px" }],
    ["color", "Color del borde", "color"],
  ],
  shadows: [
    ["sm", "Sombra chica", "text"],
    ["md", "Sombra media", "text"],
    ["lg", "Sombra grande", "text"],
    ["focus", "Sombra de foco", "text"],
  ],
};

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function mergeDesignTokens(defaults, source) {
  const input = isPlainObject(source) ? source : {};
  return Object.fromEntries(
    TOKEN_GROUPS.map(({ id }) => [
      id,
      {
        ...(defaults[id] || {}),
        ...(isPlainObject(input[id]) ? input[id] : {}),
      },
    ])
  );
}

function finite(value, fallback) {
  if (value === "" || value === null || value === undefined) return fallback;
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function cssPixels(value, fallback = 0) {
  return `${finite(value, fallback)}px`;
}

function normalizeEditableColor(value) {
  const candidate = String(value ?? "").trim();
  if (!candidate) return "";
  const lowered = candidate.toLowerCase();
  if (["transparent", "transparente", "trasparente"].includes(lowered)) return "transparent";
  return candidate;
}

function colorInputValue(value) {
  const candidate = normalizeEditableColor(value);
  if (/^#[0-9a-f]{6}$/i.test(candidate)) return candidate;
  if (/^#[0-9a-f]{3}$/i.test(candidate)) {
    return `#${candidate.slice(1).split("").map((char) => `${char}${char}`).join("")}`;
  }
  return "#000000";
}

function TokenField({ field, value, onChange }) {
  const [key, label, type, options = {}] = field;
  const common = {
    fullWidth: true,
    size: "small",
    label,
    value: value ?? "",
  };

  if (type === "select") {
    return (
      <TextField {...common} select onChange={(event) => onChange(key, event.target.value)}>
        {(options.options || []).map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (type === "number") {
    return (
      <TextField
        {...common}
        type="number"
        inputProps={{ min: options.min, max: options.max, step: options.step }}
        InputProps={
          options.suffix
            ? { endAdornment: <InputAdornment position="end">{options.suffix}</InputAdornment> }
            : undefined
        }
        onChange={(event) => {
          const nextValue = event.target.value;
          onChange(key, nextValue === "" ? "" : Number(nextValue));
        }}
      />
    );
  }

  if (type === "color") {
    const normalized = normalizeEditableColor(value);
    const transparent = normalized === "transparent";
    return (
      <TextField
        {...common}
        value={normalized}
        placeholder="transparent o #RRGGBB"
        helperText={transparent ? "Valor actual: transparent" : undefined}
        onChange={(event) => onChange(key, normalizeEditableColor(event.target.value))}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Box
                component="input"
                type="color"
                aria-label={`Elegir ${label.toLowerCase()}`}
                value={colorInputValue(normalized)}
                onChange={(event) => onChange(key, event.target.value)}
                sx={{ width: 28, height: 28, p: 0, border: 0, bgcolor: "transparent", cursor: "pointer", opacity: transparent ? 0.45 : 1 }}
              />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Button
                size="small"
                onClick={() => onChange(key, "transparent")}
                sx={{ minWidth: 0, px: 0.7, textTransform: "none", fontSize: ".68rem", fontWeight: 900 }}
              >
                Transparente
              </Button>
            </InputAdornment>
          ),
        }}
      />
    );
  }

  return <TextField {...common} onChange={(event) => onChange(key, event.target.value)} />;
}

function DesignTokenPreview({ tokens }) {
  const colors = tokens.colors || {};
  const typography = tokens.typography || {};
  const spacing = tokens.spacing || {};
  const borders = tokens.borders || {};
  const shadows = tokens.shadows || {};

  return (
    <Box
      sx={{
        p: cssPixels(spacing.lg, 24),
        bgcolor: colors.background,
        color: colors.text,
        borderRadius: cssPixels(borders.radiusLg, 18),
        border: `${finite(borders.width, 1)}px ${borders.style || "solid"} ${borders.color || colors.divider}`,
        boxShadow: shadows.md,
        fontFamily: typography.bodyFontFamily || typography.fontFamily,
      }}
    >
      <Typography
        sx={{
          color: colors.secondary,
          fontFamily: typography.headingFontFamily || typography.fontFamily,
          fontSize: `${finite(typography.baseFontSize, 16) * finite(typography.scaleRatio, 1.2) * 1.35}px`,
          lineHeight: finite(typography.headingLineHeight, 1.15),
          fontWeight: finite(typography.fontWeightBold, 800),
          letterSpacing: cssPixels(typography.letterSpacing, 0),
        }}
      >
        Vista global
      </Typography>
      <Typography
        sx={{
          mt: cssPixels(spacing.sm, 8),
          color: colors.mutedText,
          fontSize: cssPixels(typography.baseFontSize, 16),
          lineHeight: finite(typography.lineHeight, 1.5),
          fontWeight: finite(typography.fontWeightRegular, 400),
        }}
      >
        Estos valores completan el diseño general. Los colores y estilos definidos dentro de un segmento conservan prioridad.
      </Typography>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: cssPixels(spacing.md, 16) }}>
        <Box
          sx={{
            px: cssPixels(spacing.md, 16),
            py: cssPixels(spacing.sm, 8),
            bgcolor: colors.primary,
            color: colors.onPrimary,
            borderRadius: cssPixels(borders.radiusPill, 9999),
            boxShadow: shadows.sm,
            fontWeight: finite(typography.fontWeightMedium, 600),
          }}
        >
          Acción principal
        </Box>
        <Box
          sx={{
            px: cssPixels(spacing.md, 16),
            py: cssPixels(spacing.sm, 8),
            bgcolor: colors.surface,
            color: colors.secondary,
            borderRadius: cssPixels(borders.radiusPill, 9999),
            border: `${finite(borders.width, 1)}px ${borders.style || "solid"} ${borders.color || colors.divider}`,
            fontWeight: finite(typography.fontWeightMedium, 600),
          }}
        >
          Acción secundaria
        </Box>
      </Stack>
    </Box>
  );
}

function DesignTokensDrawer({ open, designTokens, theme, onClose, onChange }) {
  const [activeGroup, setActiveGroup] = React.useState("colors");
  const defaults = React.useMemo(() => buildDefaultDesignTokens(theme), [theme]);
  const tokens = React.useMemo(
    () => mergeDesignTokens(defaults, designTokens),
    [defaults, designTokens]
  );
  const group = TOKEN_GROUPS.find((item) => item.id === activeGroup) || TOKEN_GROUPS[0];

  const updateField = React.useCallback(
    (key, value) => {
      onChange?.({
        ...tokens,
        [activeGroup]: {
          ...tokens[activeGroup],
          [key]: value,
        },
      });
    },
    [activeGroup, onChange, tokens]
  );

  const updateTypographyFonts = React.useCallback(
    (headingFamily, bodyFamily) => {
      const heading = headingFamily || tokens.typography?.headingFontFamily || "Exo 2";
      const body = bodyFamily || tokens.typography?.bodyFontFamily || "Asap";
      onChange?.({
        ...tokens,
        typography: {
          ...tokens.typography,
          fontFamily: body,
          headingFontFamily: heading,
          bodyFontFamily: body,
        },
      });
    },
    [onChange, tokens]
  );

  const resetGroup = React.useCallback(() => {
    onChange?.({
      ...tokens,
      [activeGroup]: { ...defaults[activeGroup] },
    });
  }, [activeGroup, defaults, onChange, tokens]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520 },
          maxWidth: "100%",
          bgcolor: "#F7F8FA",
        },
      }}
    >
      <Stack sx={{ minHeight: "100%" }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ px: 2, py: 1.6, bgcolor: "#fff" }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Tokens globales</Typography>
            <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>
              Cambios del borrador con vista previa inmediata
            </Typography>
          </Box>
          <IconButton aria-label="Cerrar tokens globales" onClick={onClose}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        <Tabs
          value={activeGroup}
          onChange={(_event, value) => setActiveGroup(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ bgcolor: "#fff", borderTop: "1px solid rgba(25,36,47,.06)", borderBottom: "1px solid rgba(25,36,47,.08)" }}
        >
          {TOKEN_GROUPS.map((item) => (
            <Tab key={item.id} value={item.id} label={item.label} sx={{ textTransform: "none", fontWeight: 850 }} />
          ))}
        </Tabs>

        <Box sx={{ flex: 1, overflow: "auto", p: { xs: 1.5, sm: 2 } }}>
          <Stack spacing={2}>
            <Box>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                <Box>
                  <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>{group.label}</Typography>
                  <Typography variant="body2" sx={{ mt: 0.3, color: "rgba(25,36,47,.62)" }}>
                    {group.description}
                  </Typography>
                </Box>
                <Tooltip title="Restablecer este grupo">
                  <IconButton size="small" onClick={resetGroup} sx={{ border: "1px solid rgba(25,36,47,.12)" }}>
                    <RestartAltRoundedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Box>

            {activeGroup === "typography" ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                  gap: 1.25,
                  p: 1.5,
                  borderRadius: 3,
                  bgcolor: "#fff",
                  border: "1px solid rgba(25,36,47,.08)",
                }}
              >
                <GoogleFontPicker
                  label="Tipografía de títulos"
                  value={tokens.typography?.headingFontFamily}
                  onChange={(family) => updateTypographyFonts(family, tokens.typography?.bodyFontFamily)}
                />
                <GoogleFontPicker
                  label="Tipografía de textos"
                  value={tokens.typography?.bodyFontFamily}
                  onChange={(family) => updateTypographyFonts(tokens.typography?.headingFontFamily, family)}
                />
              </Box>
            ) : null}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                gap: 1.25,
                p: 1.5,
                borderRadius: 3,
                bgcolor: "#fff",
                border: "1px solid rgba(25,36,47,.08)",
              }}
            >
              {(TOKEN_FIELDS[activeGroup] || []).map((field) => (
                <TokenField
                  key={field[0]}
                  field={field}
                  value={tokens[activeGroup]?.[field[0]]}
                  onChange={updateField}
                />
              ))}
            </Box>

            <Divider />
            <Box>
              <Typography variant="overline" sx={{ color: "rgba(25,36,47,.55)", fontWeight: 900 }}>
                Muestra en vivo
              </Typography>
              <DesignTokenPreview tokens={tokens} />
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Drawer>
  );
}

export default function LandingStudioToolbar({
  title,
  publicStatus,
  revision,
  draftRevision,
  publishedRevision,
  deployment = null,
  deploymentStatus = "",
  deploymentJob = null,
  deploymentError = "",
  deploymentChecking = false,
  deploymentAction = "",
  device,
  saving,
  publishing,
  statusChanging = false,
  previewing,
  openingPreview,
  hasUnsavedChanges,
  publicationValidation = null,
  publicationValidationLoading = false,
  publicationValidationError = "",
  accessibilityIssueCount = 0,
  canOpenPublic,
  designTokens,
  theme,
  onBack,
  onChangeDevice,
  onOpenSettings,
  onOpenSections,
  onChangeDesignTokens,
  onSaveDraft,
  onTogglePreview,
  onOpenSecurePreview,
  onValidatePublication,
  onOpenAccessibility,
  onPublish,
  onOpenPublic,
  onRefreshDeployment,
  onRetryDeployment,
  canUndo = false,
  canRedo = false,
  onUndo,
  onRedo,
  onOpenHistory,
}) {
  const [tokensOpen, setTokensOpen] = React.useState(false);
  const [secondaryAnchorEl, setSecondaryAnchorEl] = React.useState(null);
  const [deviceAnchorEl, setDeviceAnchorEl] = React.useState(null);
  const [publishOpen, setPublishOpen] = React.useState(false);
  const previewDevice = getLandingPreviewDevice(device);
  const statusLabel =
    publicStatus === "published"
      ? "Publicada"
      : publicStatus === "maintenance"
        ? "Mantenimiento"
        : "Borrador";
  const busy = saving || publishing || statusChanging || openingPreview;
  const resolvedDraftRevision = draftRevision || revision || 1;
  const resolvedPublishedRevision = publishedRevision || 0;
  const resolvedAccessibilityIssueCount = Math.max(0, Number(accessibilityIssueCount) || 0);
  const publicationIsCurrent =
    publicStatus === "published" &&
    resolvedPublishedRevision === resolvedDraftRevision &&
    !hasUnsavedChanges;
  const resolvedDeployment = deployment || (deploymentStatus ? { status: deploymentStatus } : {});
  const deploymentView = deploymentPresentation(
    resolvedDeployment,
    deploymentJob,
    deploymentError
  );
  const deploymentRetryAllowed =
    Boolean(onRetryDeployment) &&
    (deploymentView.status === "failed" ||
      ["not_requested", "not_configured"].includes(deploymentView.status));
  const publishDisabled = busy || previewing || hasUnsavedChanges || publicationIsCurrent;
  const blockingIssues = Array.isArray(publicationValidation?.blockers) ? publicationValidation.blockers : [];
  const recommendationIssues = Array.isArray(publicationValidation?.recommendations) ? publicationValidation.recommendations : [];
  const publicationValidationReady = publicationValidation?.canPublish === true || blockingIssues.length > 0;
  const confirmPublishDisabled = publishDisabled || publicationValidationLoading || !publicationValidationReady || blockingIssues.length > 0;
  const publishTooltip = hasUnsavedChanges
    ? "Guardá el borrador antes de publicar"
    : previewing
      ? "Volvé al editor para publicar"
      : publicationIsCurrent
        ? "Este borrador ya es la versión publicada"
        : "Revisar y publicar el borrador actual";
  const saveStateLabel = saving ? "Guardando" : hasUnsavedChanges ? "Sin guardar" : "Guardado";
  const saveStateTooltip = saving
    ? `Guardando el borrador v${resolvedDraftRevision}`
    : hasUnsavedChanges
      ? `Borrador v${resolvedDraftRevision} con cambios pendientes`
      : `Borrador v${resolvedDraftRevision} guardado · Publicada v${resolvedPublishedRevision}`;

  const openPublishDialog = async () => {
    setPublishOpen(true);
    await onValidatePublication?.();
  };

  const confirmPublish = async () => {
    const result = await onPublish?.();
    if (result) setPublishOpen(false);
  };

  return (
    <>
      <Box
        component="header"
        sx={{
          position: "fixed",
          inset: "0 0 auto 0",
          zIndex: 1100,
          minHeight: 64,
          px: { xs: 1, sm: 1.5 },
          py: 1,
          bgcolor: "rgba(255,255,255,.96)",
          borderBottom: "1px solid rgba(25,36,47,.10)",
          backdropFilter: "blur(18px)",
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
            <Tooltip title="Volver al panel">
              <IconButton aria-label="Volver al panel" onClick={onBack} size="small" sx={{ border: "1px solid rgba(25,36,47,.12)" }}>
                <ArrowBackRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 950, color: BRAND.dark, lineHeight: 1.08 }} noWrap>
                {title || "Editor visual"}
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(25,36,47,.55)", display: { xs: "none", sm: "block" } }}>
                {statusChanging
                  ? "Actualizando el estado público con el servidor"
                  : previewing
                    ? "Vista previa del borrador: no se está publicando"
                    : hasUnsavedChanges
                    ? "Hay cambios locales pendientes de guardar"
                    : "Editá el borrador y publicalo cuando esté listo"}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.65}>
            <Tooltip title={`Vista: ${previewDevice.label}. Cambiar dispositivo`}>
              <IconButton
                size="small"
                onClick={(event) => setDeviceAnchorEl(event.currentTarget)}
                aria-label={`Vista ${previewDevice.label}. Cambiar dispositivo`}
                sx={{
                  display: { xs: "inline-flex", sm: "none" },
                  border: "1px solid rgba(25,36,47,.12)",
                  color: BRAND.orange,
                }}
              >
                {previewDevice.id === "desktop" ? (
                  <DesktopWindowsRoundedIcon fontSize="small" />
                ) : previewDevice.id === "tablet" ? (
                  <TabletMacRoundedIcon fontSize="small" />
                ) : (
                  <PhoneIphoneRoundedIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={deviceAnchorEl}
              open={Boolean(deviceAnchorEl)}
              onClose={() => setDeviceAnchorEl(null)}
              MenuListProps={{ "aria-label": "Elegir dispositivo de previsualización" }}
            >
              {[
                ["desktop", "Escritorio", DesktopWindowsRoundedIcon],
                ["tablet", "Tablet", TabletMacRoundedIcon],
                ["mobile", "Celular", PhoneIphoneRoundedIcon],
              ].map(([id, label, DeviceIcon]) => (
                <MenuItem
                  key={id}
                  selected={previewDevice.id === id}
                  onClick={() => {
                    onChangeDevice(id);
                    setDeviceAnchorEl(null);
                  }}
                  sx={{ gap: 1.1 }}
                >
                  <DeviceIcon fontSize="small" />
                  {label}
                </MenuItem>
              ))}
            </Menu>

            <Stack
              direction="row"
              sx={{
                p: 0.35,
                borderRadius: 999,
                bgcolor: "rgba(25,36,47,.06)",
                display: { xs: "none", sm: "flex" },
              }}
            >
              <Tooltip title="Vista de computadora">
                <IconButton
                  size="small"
                  aria-label="Vista de computadora"
                  onClick={() => onChangeDevice("desktop")}
                  sx={{
                    bgcolor: previewDevice.id === "desktop" ? "#fff" : "transparent",
                    color: previewDevice.id === "desktop" ? BRAND.orange : "rgba(25,36,47,.56)",
                    boxShadow: previewDevice.id === "desktop" ? "0 2px 8px rgba(25,36,47,.10)" : "none",
                  }}
                >
                  <DesktopWindowsRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Vista de tablet">
                <IconButton
                  size="small"
                  aria-label="Vista de tablet"
                  onClick={() => onChangeDevice("tablet")}
                  sx={{
                    bgcolor: previewDevice.id === "tablet" ? "#fff" : "transparent",
                    color: previewDevice.id === "tablet" ? BRAND.orange : "rgba(25,36,47,.56)",
                    boxShadow: previewDevice.id === "tablet" ? "0 2px 8px rgba(25,36,47,.10)" : "none",
                  }}
                >
                  <TabletMacRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Vista de celular">
                <IconButton
                  size="small"
                  aria-label="Vista de celular"
                  onClick={() => onChangeDevice("mobile")}
                  sx={{
                    bgcolor: previewDevice.id === "mobile" ? "#fff" : "transparent",
                    color: previewDevice.id === "mobile" ? BRAND.orange : "rgba(25,36,47,.56)",
                    boxShadow: previewDevice.id === "mobile" ? "0 2px 8px rgba(25,36,47,.10)" : "none",
                  }}
                >
                  <PhoneIphoneRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            <Tooltip title={saveStateTooltip}>
              <Chip
                size="small"
                aria-label={saveStateLabel}
                icon={saving ? <SyncRoundedIcon /> : hasUnsavedChanges ? <SaveRoundedIcon /> : <CloudDoneRoundedIcon />}
                label={saveStateLabel}
                color={hasUnsavedChanges ? "warning" : "default"}
                variant={hasUnsavedChanges ? "filled" : "outlined"}
                sx={{
                  width: { xs: 34, sm: 34, md: "auto" },
                  minWidth: { xs: 34, sm: 34, md: "auto" },
                  fontWeight: 900,
                  bgcolor: hasUnsavedChanges ? undefined : "rgba(255,255,255,.72)",
                  "& .MuiChip-label": {
                    display: { xs: "none", md: "block" },
                    px: { md: 0.8 },
                  },
                  "& .MuiChip-icon": {
                    mx: { xs: "auto", sm: "auto", md: 0.55 },
                  },
                }}
              />
            </Tooltip>

            {!previewing ? (
              <Tooltip title="Agregar, mostrar u ocultar contenido de la página">
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddRoundedIcon />}
                    disabled={busy}
                    aria-label="Agregar, mostrar u ocultar contenido de la página"
                    onClick={onOpenSections}
                    sx={{
                      borderRadius: 999,
                      px: { xs: 1, lg: 1.35 },
                      minWidth: { xs: 38, lg: "auto" },
                      textTransform: "none",
                      fontWeight: 900,
                      color: BRAND.dark,
                      borderColor: "rgba(25,36,47,.18)",
                      "& .MuiButton-startIcon": { mr: { xs: 0, lg: 0.7 } },
                    }}
                  >
                    <Box component="span" sx={{ display: { xs: "none", lg: "inline" } }}>
                      Agregar contenido
                    </Box>
                  </Button>
                </span>
              </Tooltip>
            ) : null}

            <Tooltip title={previewing ? "Volver a editar" : "Previsualizar el borrador sin publicarlo"}>
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={previewing ? <EditRoundedIcon /> : <VisibilityRoundedIcon />}
                  disabled={busy}
                  aria-label={previewing ? "Volver a editar" : "Previsualizar borrador"}
                  onClick={onTogglePreview}
                  sx={{
                    borderRadius: 999,
                    px: { xs: 1, md: 1.4 },
                    minWidth: { xs: 38, md: "auto" },
                    textTransform: "none",
                    fontWeight: 900,
                    color: BRAND.dark,
                    borderColor: "rgba(25,36,47,.18)",
                    "& .MuiButton-startIcon": { mr: { xs: 0, md: 0.7 } },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                    {previewing ? "Editar" : "Preview"}
                  </Box>
                </Button>
              </span>
            </Tooltip>

            {!previewing ? (
              <>
                <Tooltip title={hasUnsavedChanges ? "Guardar cambios únicamente en el borrador" : "El borrador ya está guardado"}>
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveRoundedIcon />}
                      disabled={busy || !hasUnsavedChanges}
                      aria-label={hasUnsavedChanges ? "Guardar cambios del borrador" : "Borrador guardado"}
                      onClick={onSaveDraft}
                      sx={{
                        borderRadius: 999,
                        px: { xs: 1, md: 1.4 },
                        minWidth: { xs: 38, md: "auto" },
                        textTransform: "none",
                        fontWeight: 900,
                        color: BRAND.dark,
                        borderColor: "rgba(25,36,47,.18)",
                        "& .MuiButton-startIcon": { mr: { xs: 0, md: 0.7 } },
                      }}
                    >
                      <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                        Guardar
                      </Box>
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip title={resolvedAccessibilityIssueCount ? `${resolvedAccessibilityIssueCount} señales de accesibilidad para revisar` : "Revisar accesibilidad del borrador"}>
                  <span>
                    <IconButton
                      size="small"
                      aria-label={resolvedAccessibilityIssueCount ? `Revisar accesibilidad. ${resolvedAccessibilityIssueCount} señales detectadas` : "Revisar accesibilidad"}
                      disabled={busy}
                      onClick={onOpenAccessibility}
                      sx={{
                        display: { xs: "none", md: "inline-flex" },
                        border: "1px solid rgba(25,36,47,.12)",
                        color: resolvedAccessibilityIssueCount ? "warning.main" : "rgba(25,36,47,.62)",
                      }}
                    >
                      <Badge badgeContent={resolvedAccessibilityIssueCount} color="warning" max={9}>
                        <AccessibilityNewRoundedIcon fontSize="small" />
                      </Badge>
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip title={publishTooltip}>
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={publishing ? <CircularProgress size={15} color="inherit" /> : <RocketLaunchRoundedIcon />}
                      disabled={publishDisabled}
                      aria-label="Publicar cambios"
                      onClick={openPublishDialog}
                      sx={{
                        borderRadius: 999,
                        px: { xs: 1, md: 1.5 },
                        minWidth: { xs: 38, md: "auto" },
                        textTransform: "none",
                        fontWeight: 950,
                        bgcolor: BRAND.orange,
                        "&:hover": { bgcolor: "#DE4D30" },
                        "& .MuiButton-startIcon": { mr: { xs: 0, md: 0.7 } },
                      }}
                    >
                      <Box component="span" sx={{ display: { xs: "none", md: "inline" } }}>
                        Publicar
                      </Box>
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip title="Más opciones del editor">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Más opciones del editor"
                      aria-haspopup="menu"
                      aria-expanded={Boolean(secondaryAnchorEl) ? "true" : undefined}
                      disabled={busy}
                      onClick={(event) => setSecondaryAnchorEl(event.currentTarget)}
                      sx={{ border: "1px solid rgba(25,36,47,.12)" }}
                    >
                      <MoreHorizRoundedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            ) : null}
          </Stack>
        </Stack>
      </Box>

      <Menu
        anchorEl={secondaryAnchorEl}
        open={Boolean(secondaryAnchorEl)}
        onClose={() => setSecondaryAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { mt: 0.75, minWidth: 245, borderRadius: 2.5 } } }}
      >
        <MenuItem
          disabled={!canUndo}
          onClick={() => {
            setSecondaryAnchorEl(null);
            onUndo?.();
          }}
        >
          <UndoRoundedIcon fontSize="small" sx={{ mr: 1.2, color: canUndo ? BRAND.orange : "inherit" }} />
          Deshacer último cambio
        </MenuItem>
        <MenuItem
          disabled={!canRedo}
          onClick={() => {
            setSecondaryAnchorEl(null);
            onRedo?.();
          }}
        >
          <RedoRoundedIcon fontSize="small" sx={{ mr: 1.2 }} />
          Rehacer cambio
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSecondaryAnchorEl(null);
            onOpenHistory?.();
          }}
        >
          <HistoryRoundedIcon fontSize="small" sx={{ mr: 1.2 }} />
          Recuperar una publicación
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setSecondaryAnchorEl(null);
            onOpenAccessibility?.();
          }}
        >
          <AccessibilityNewRoundedIcon fontSize="small" sx={{ mr: 1.2, color: resolvedAccessibilityIssueCount ? "warning.main" : BRAND.orange }} />
          <Box component="span" sx={{ flex: 1 }}>Revisar accesibilidad</Box>
          {resolvedAccessibilityIssueCount ? (
            <Chip
              size="small"
              label={resolvedAccessibilityIssueCount}
              color="warning"
              sx={{ height: 22, ml: 1, fontWeight: 950 }}
            />
          ) : null}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSecondaryAnchorEl(null);
            setTokensOpen(true);
          }}
        >
          <PaletteRoundedIcon fontSize="small" sx={{ mr: 1.2, color: BRAND.orange }} />
          Diseño global
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSecondaryAnchorEl(null);
            onOpenSettings?.(0);
          }}
        >
          <SettingsRoundedIcon fontSize="small" sx={{ mr: 1.2 }} />
          Preferencias del sitio
        </MenuItem>
        <MenuItem
          onClick={() => {
            setSecondaryAnchorEl(null);
            onOpenSettings?.(4);
          }}
        >
          <RocketLaunchRoundedIcon fontSize="small" sx={{ mr: 1.2 }} />
          SEO y estado público
        </MenuItem>
        <MenuItem disabled sx={{ opacity: "1 !important" }}>
          <VisibilityRoundedIcon fontSize="small" sx={{ mr: 1.2, color: "text.secondary" }} />
          Estado público: {statusLabel}
        </MenuItem>
        <Divider />
        <MenuItem disabled sx={{ opacity: "1 !important" }}>
          {deploymentView.status === "failed" ? (
            <ErrorOutlineRoundedIcon fontSize="small" sx={{ mr: 1.2, color: "error.main" }} />
          ) : (
            <CloudDoneRoundedIcon fontSize="small" sx={{ mr: 1.2, color: "text.secondary" }} />
          )}
          Despliegue: {deploymentView.label}
        </MenuItem>
        <MenuItem
          disabled={deploymentChecking || Boolean(deploymentAction)}
          onClick={() => {
            setSecondaryAnchorEl(null);
            onRefreshDeployment?.();
          }}
        >
          {deploymentChecking ? (
            <CircularProgress size={18} sx={{ mr: 1.2 }} />
          ) : (
            <RefreshRoundedIcon fontSize="small" sx={{ mr: 1.2 }} />
          )}
          Actualizar estado del despliegue
        </MenuItem>
        {deploymentRetryAllowed ? (
          <MenuItem
            disabled={Boolean(deploymentAction)}
            onClick={() => {
              setSecondaryAnchorEl(null);
              onRetryDeployment?.();
            }}
          >
            {deploymentAction === "retry" ? (
              <CircularProgress size={18} sx={{ mr: 1.2 }} />
            ) : (
              <ReplayRoundedIcon fontSize="small" sx={{ mr: 1.2 }} />
            )}
            Reintentar despliegue
          </MenuItem>
        ) : null}
        <Divider />
        <MenuItem
          disabled={!canOpenPublic || openingPreview}
          onClick={() => {
            setSecondaryAnchorEl(null);
            onOpenSecurePreview?.();
          }}
        >
          {openingPreview ? (
            <CircularProgress size={18} sx={{ mr: 1.2 }} />
          ) : (
            <VisibilityRoundedIcon fontSize="small" sx={{ mr: 1.2 }} />
          )}
          Preview seguro
        </MenuItem>
        <MenuItem
          disabled={!canOpenPublic}
          onClick={() => {
            setSecondaryAnchorEl(null);
            onOpenPublic?.();
          }}
        >
          <OpenInNewRoundedIcon fontSize="small" sx={{ mr: 1.2 }} />
          Abrir versión publicada
        </MenuItem>
      </Menu>

      <Dialog
        open={publishOpen}
        onClose={publishing ? undefined : () => setPublishOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 950, color: BRAND.dark }}>
          Publicar borrador v{resolvedDraftRevision}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.6}>
            <Alert severity={publicationIsCurrent ? "success" : "info"} sx={{ borderRadius: 2.5 }}>
              {publicationIsCurrent
                ? "Este borrador ya coincide con la versión publicada. No hace falta crear otra revisión."
                : `Se publicará exactamente el borrador guardado v${resolvedDraftRevision}. La versión pública actual es v${resolvedPublishedRevision || 0}.`}
            </Alert>

            {publicationValidationLoading ? (
              <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ borderRadius: 2.5 }}>
                Revisando enlaces, medios y configuraciones del borrador guardado…
              </Alert>
            ) : publicationValidationError ? (
              <Alert severity="error" sx={{ borderRadius: 2.5 }}>
                {publicationValidationError}
              </Alert>
            ) : blockingIssues.length > 0 ? (
              <Alert severity="error" sx={{ borderRadius: 2.5 }}>
                Hay {blockingIssues.length} {blockingIssues.length === 1 ? "problema que bloquea" : "problemas que bloquean"} la publicación.
              </Alert>
            ) : publicationValidation?.canPublish === true ? (
              <Alert severity={recommendationIssues.length ? "warning" : "success"} sx={{ borderRadius: 2.5 }}>
                {recommendationIssues.length
                  ? `No hay errores bloqueantes. Hay ${recommendationIssues.length} recomendación${recommendationIssues.length === 1 ? "" : "es"} opcional${recommendationIssues.length === 1 ? "" : "es"}.`
                  : "Validación completa: no se detectaron problemas que impidan publicar."}
              </Alert>
            ) : null}

            {blockingIssues.map((issue, index) => (
              <Box key={`block-${issue.code || index}-${issue.path || index}`} sx={{ p: 1.4, borderRadius: 2.5, border: "1px solid", borderColor: "error.light", bgcolor: "rgba(211,47,47,.04)" }}>
                <Typography sx={{ fontWeight: 950, color: "error.main" }}>{issue.title || "Problema bloqueante"}</Typography>
                <Typography variant="body2" sx={{ mt: 0.35, color: "rgba(25,36,47,.72)" }}>{issue.message}</Typography>
                {issue.action ? <Typography variant="body2" sx={{ mt: 0.55, fontWeight: 850, color: BRAND.dark }}>{issue.action}</Typography> : null}
                {issue.path ? <Typography variant="caption" sx={{ display: "block", mt: 0.45, color: "rgba(25,36,47,.48)" }}>{issue.path}</Typography> : null}
              </Box>
            ))}

            {recommendationIssues.length > 0 ? (
              <Box sx={{ p: 1.4, borderRadius: 2.5, border: "1px solid rgba(237,108,2,.28)", bgcolor: "rgba(237,108,2,.035)" }}>
                <Typography sx={{ fontWeight: 950, color: BRAND.dark }}>Recomendaciones opcionales</Typography>
                <Stack spacing={1} sx={{ mt: 0.8 }}>
                  {recommendationIssues.map((issue, index) => (
                    <Box key={`recommendation-${issue.code || index}-${issue.path || index}`}>
                      <Typography variant="body2" sx={{ fontWeight: 850, color: BRAND.dark }}>{issue.title}</Typography>
                      <Typography variant="body2" sx={{ color: "rgba(25,36,47,.66)" }}>{issue.message}</Typography>
                      {issue.action ? <Typography variant="caption" sx={{ color: "rgba(25,36,47,.58)" }}>{issue.action}</Typography> : null}
                    </Box>
                  ))}
                </Stack>
              </Box>
            ) : null}

            <Box
              sx={{
                p: 1.5,
                borderRadius: 2.5,
                border: "1px solid rgba(25,36,47,.10)",
                bgcolor: "rgba(25,36,47,.025)",
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ fontWeight: 900, color: BRAND.dark }}>
                    Estado del despliegue
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.25, color: "rgba(25,36,47,.62)" }}>
                    {deploymentView.description}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={deploymentView.label}
                  color={deploymentView.severity}
                  variant={deploymentView.status === "live" ? "filled" : "outlined"}
                  sx={{ flexShrink: 0, fontWeight: 900 }}
                />
              </Stack>
            </Box>

            {deploymentView.error ? (
              <Alert severity="error" sx={{ borderRadius: 2.5 }}>
                {deploymentView.error}
              </Alert>
            ) : null}

            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.66)" }}>
              Al confirmar, el backend validará el borrador, creará la revisión pública sólo si cambió
              y, cuando el proyecto tenga despliegue automático, iniciará su actualización.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, flexWrap: "wrap" }}>
          {deploymentRetryAllowed && publicationIsCurrent ? (
            <Button
              onClick={onRetryDeployment}
              disabled={Boolean(deploymentAction) || publishing}
              startIcon={deploymentAction === "retry" ? <CircularProgress size={16} /> : <ReplayRoundedIcon />}
              sx={{ mr: "auto", textTransform: "none", fontWeight: 850 }}
            >
              Reintentar despliegue
            </Button>
          ) : null}
          <Button
            onClick={() => setPublishOpen(false)}
            disabled={publishing}
            sx={{ textTransform: "none", fontWeight: 850 }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={confirmPublish}
            disabled={confirmPublishDisabled}
            startIcon={publishing ? <CircularProgress size={16} color="inherit" /> : <RocketLaunchRoundedIcon />}
            sx={{
              borderRadius: 999,
              textTransform: "none",
              fontWeight: 950,
              bgcolor: BRAND.orange,
              "&:hover": { bgcolor: "#DE4D30" },
            }}
          >
            {publishing ? "Publicando…" : "Confirmar publicación"}
          </Button>
        </DialogActions>
      </Dialog>

      <DesignTokensDrawer
        open={tokensOpen}
        designTokens={designTokens}
        theme={theme}
        onClose={() => setTokensOpen(false)}
        onChange={onChangeDesignTokens}
      />
    </>
  );
}
